import {
    AllowedURLParamTypes, DMParameters,
    URLParamArrayElement
} from "../custom-types/custom-types";
import { MarketingClientConstants } from "../constants/marketing-client-constants";
import { BannedEmailDomains } from "../constants/banned-email-domains";
import { validEmailRegexp } from "../constants/utils-constants";
import { IdObjectConstants } from "../constants/id-object-constants";
import { CtaObjectConstants } from "../constants/cta-object-constants";
import { DOMHelper } from "../helpers/dom-helper";
import { EventType } from "../constants/event-type-constants";
import { RwObjectConstants } from "../constants/rw-object.constants";

export class Utils {
    static isNullOrUndefined(value?: any): boolean {
        return value === null || value === undefined;
    }

    static isObject(value?: any): boolean {
        return !Utils.isNullOrUndefined(value) && typeof value === "object" && !(value instanceof Array);
    }

    static isArray(value?: any): boolean {
        return !Utils.isNullOrUndefined(value) && value instanceof Array;
    }

    static stringNotEmpty(value?: string | null): boolean {
        return (
            !Utils.isNullOrUndefined(value) &&
            typeof value === "string" &&
            value.length > 0
        );
    }

    static getURLParamsAsArray(): Array<URLParamArrayElement> {
        const searchString = window.location.search.substring(1),
            searchStringSplit = searchString.split("&");

        return searchStringSplit.map(keyValuePair => {
            const [rawKey, rawValue] = keyValuePair.split("="),
                output = {
                    key: rawKey,
                    value: null
                } as URLParamArrayElement;

            switch (rawValue) {
                case "true":
                    output.value = true;
                    break;
                case "false":
                    output.value = false;
                    break;
                case "null":
                    output.value = null;
                    break;
                case "undefined":
                case "":
                    output.value = undefined;
                    break;
                default:
                    if (isNaN(Number(rawValue))) {
                        try {
                            output.value = decodeURIComponent(rawValue);
                        } catch (error) {
                            output.value = rawValue;
                        }
                    } else {
                        output.value = Number(rawValue);
                    }
                    break;
            }

            return output;
        });
    }

    static getURLParamsAsObject(): { [key: string]: AllowedURLParamTypes } {
        const urlParamsArray = Utils.getURLParamsAsArray(),
            output = {};

        urlParamsArray.forEach((param: URLParamArrayElement) => {
            output[param.key] = param.value;
        });

        return output;
    }

    static getDMParams(): DMParameters | null {
        const supposedDMParameters = window[MarketingClientConstants.dmParametersGlobalKey],
            resultDMParameters = {};

        if (Utils.isNullOrUndefined(supposedDMParameters)) {
            return null;
        }

        Object.keys(supposedDMParameters).forEach(key => {
            if (!Utils.isObject(supposedDMParameters[key]) && supposedDMParameters[key] !== undefined) {
                resultDMParameters[key] = supposedDMParameters[key];
            }
        });

        return resultDMParameters as DMParameters;
    }

    static getURLParamFromEventTarget(urlWithParams: string, name: string): string {
        // return decodeURIComponent(new RegExp("[?|&]" + name + "=" + "([^&;]+?)(&|#|;|$)").exec(urlWithParams.search) || [, ""])[1].replace(/\+/g, "%20");

        if (urlWithParams) {
            const url = new URL(urlWithParams),
                urlParams = new URLSearchParams(url.search || "");

            if (urlParams.has(name)) {
                return urlParams.get(name) || "";
            }


        }
        return "";
    }

    static getBigDataVariable(cookieName: string, bigDataName: string) {
        const cookieValue = Utils.getCookie(cookieName),
            bigDataVariable = (
                window["DataOcean"] &&
                window["DataOcean"].BluewhalePortal &&
                window["DataOcean"].BluewhalePortal.bwTrck &&
                window["DataOcean"].BluewhalePortal.bwTrck[bigDataName]
            ) ? window["DataOcean"].BluewhalePortal.bwTrck[bigDataName] : null;

        if (cookieValue !== null || !!bigDataVariable) {
            return cookieValue || bigDataVariable;
        }

        return "";
    }

    static getCookie(name: string) {
        const nameEQ = name + "=",
            cookies = document.cookie.split(";");

        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];

            while (cookie.charAt(0) == " ") {
                cookie = cookie.substring(1, cookie.length);
            }

            if (cookie.indexOf(nameEQ) === 0) {
                return cookie.substring(nameEQ.length, cookie.length);
            }
        }
        return null;
    }

    static validateEmail(email: string) {
        const domain = email.split("@")[1];

        if (!email.length || !validEmailRegexp.test(email.toLowerCase()) || BannedEmailDomains.indexOf(domain) !== -1) {
            return false;
        }

        return true;
    }

    static showContainer(element: HTMLElement | null, className = MarketingClientConstants.hiddenClassName) {
        if (element) {
            element.classList.remove(className);
        }
    }

    static hideContainer(element: HTMLElement | null, className = MarketingClientConstants.hiddenClassName) {
        if (element) {
            element.classList.add(className);
        }
    }

    static prepareCtaObject(element: Element): CtaObject {
        const data: CtaObject = {},
            ctaElement: Element | null = DOMHelper.querySelector(`[${CtaObjectConstants.dataCta}], [${CtaObjectConstants.dataCtaCategory}]`, element),
            cta: string | null | undefined = ctaElement?.getAttribute(CtaObjectConstants.dataCta) || element.getAttribute(CtaObjectConstants.dataCta),
            ctaCategory: string | null | undefined = ctaElement?.getAttribute(CtaObjectConstants.dataCtaCategory) || element.getAttribute(CtaObjectConstants.dataCtaCategory);

        if (cta && Utils.stringNotEmpty(cta)) {
            data.cta = cta;
        }

        if (ctaCategory && Utils.stringNotEmpty(ctaCategory)) {
            data.ctaCategory = ctaCategory;
        }

        return data;
    }

    static prepareCollectionIdsObject(element: Element): CollectionIdsObject {
        const data: CollectionIdsObject = {},
            elementCampaignId: string | null = element.getAttribute(IdObjectConstants.dataCampaignId),
            elementActionId: string | null = element.getAttribute(IdObjectConstants.dataActionId),
            elementTouchPointId: string | null = element.getAttribute(IdObjectConstants.dataTouchPointId);

        if (elementCampaignId) {
            data.campaignId = elementCampaignId;
        }

        if (elementActionId) {
            data.actionId = elementActionId;
        }

        if (elementTouchPointId) {
            data.touchPointId = elementTouchPointId;
        }

        return data;
    }

    static prepareRegistrationWallObject(element: Element): RegistrationWallObject {
        const data: RegistrationWallObject = {},
            rwElement: Element | null = DOMHelper.querySelector(`[${RwObjectConstants.dataArticleCounter}], [${RwObjectConstants.dataStage}], [${RwObjectConstants.dataStageEndDate}]`, element),
            articleCounter: string | null | undefined = rwElement?.getAttribute(RwObjectConstants.dataArticleCounter),
            stage: string | null | undefined = rwElement?.getAttribute(RwObjectConstants.dataStage),
            stageEndDate: string | null | undefined = rwElement?.getAttribute(RwObjectConstants.dataStageEndDate);

        if (articleCounter) {
            data.articleCounter = articleCounter;
        }

        if (stage) {
            data.stage = stage;
        }

        if (stageEndDate) {
            data.stageEndDate = stageEndDate;
        }

        return data
    }

    static smoothScrollTo(top: number): void {
        window.scrollTo({
            top,
            behavior: "smooth"
        });
    }

    static attributeToNumber(attribute: string | undefined | null): number | undefined {
        if (!attribute || !attribute.length) {
            return undefined;
        }

        const value = parseInt(attribute, 10);

        return !Number.isNaN(value) ? value : undefined;
    }

    static showElementAfterTimeout(doAfter: Function, timeout: number | undefined) {
        let showBoxTimeout: number,
            boxIsVisible = false,
            timeoutInMs = 0;

        if (timeout) {
            timeoutInMs = timeout * 1000;
        }

        function setTimeout() {
            showBoxTimeout = window.setTimeout(function () {
                showElement();
            }, timeoutInMs);
        }

        function resetTimeout() {
            window.clearTimeout(showBoxTimeout);
        }

        function activeListener() {
            if (!boxIsVisible) {
                resetTimeout();
                setTimeout();
            }
        }

        function showElement() {
            boxIsVisible = true;

            doAfter();

            document.removeEventListener(EventType.MOUSEWHEEL, activeListener);
            document.removeEventListener(EventType.MOUSEMOVE, activeListener);
        }


        document.addEventListener(EventType.MOUSEWHEEL, activeListener);
        document.addEventListener(EventType.MOUSEMOVE, activeListener);
        setTimeout();
    }

    static changeFormatImg(element: string, type: string){
        if(element){
            return element.replace("X", `${type}`);
        }
    }
}

export interface CtaObject {
    cta?: string;
    ctaCategory?: string;
}

export interface CollectionIdsObject {
    campaignId?: string;
    actionId?: string;
    touchPointId?: string;
}

export interface RegistrationWallObject {
    articleCounter?: string,
    stage?: string,
    stageEndDate?: string
}